// Draws — the BEAD Finance cockpit. Reconciles three axes of a draw:
//   physical completion (passings lit) → auto-completes construction milestones
//   → unlocks draw ceilings → against which staged draw requests are submitted.
// Draw requests are staged automatically when a section is energized on the
// As-built map (bead_draw); this page is where they move to the state and get paid.

import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import DocumentUploader from '../components/DocumentUploader';

type Project = { id: string; project_name: string; project_code: string };
type Obligation = {
  id: string; code: string; name: string; cadence_rule: string;
  source_cite: string | null; due_date: string;
  status: 'pending' | 'in_progress' | 'submitted' | 'accepted' | 'rejected' | 'overdue' | 'na';
};
type Milestone = {
  id: string; seq: number; name: string; description?: string;
  draw_pct: number | null; category?: string; auto?: boolean; auto_threshold?: number;
  target_date: string | null; completed_date: string | null;
  status: 'pending' | 'in_progress' | 'submitted' | 'approved' | 'rejected';
  effective_completed?: boolean; draw_usd?: number;
  evidence_required?: string[] | null; notes?: any;
};
type DrawReq = {
  id: string; phase_id: string | null; phase_name: string | null; passings: number;
  federal_usd: number; match_usd: number; total_usd: number;
  status: string; payment_status: string; submitted_at: string | null; paid_at: string | null;
  ready_reference: string | null; created_at: string;
  actual_cost_usd?: number; actual_federal_usd?: number;
};
type DrawsSummary = {
  project: {
    id: string; project_code?: string; project_name: string;
    grant_amount_usd: number | null; match_amount_usd: number | null;
    total_project_cost_usd: number | null; federal_share_usd: number | null; match_share_usd: number | null;
    awarded_bsl_count?: number;
  };
  energization?: { total: number; lit: number; pct: number };
  milestones: Milestone[];
  draws?: DrawReq[];
  kpis?: {
    federal: number; match: number; total: number;
    unlocked_pct: number; unlocked_usd: number;
    requested_usd: number; paid_usd: number; staged_usd: number; available_usd: number;
    actual_cost?: number; actual_federal?: number;
  };
};

const STATUS_BADGE: Record<string, { bg: string; fg: string; label: string }> = {
  pending: { bg: '#f1f5f9', fg: '#475569', label: 'Pending' },
  in_progress: { bg: '#fef3c7', fg: '#92400e', label: 'In progress' },
  submitted: { bg: '#dbeafe', fg: '#1e40af', label: 'Submitted' },
  in_review: { bg: '#e0e7ff', fg: '#3730a3', label: 'In review' },
  accepted: { bg: '#d1fae5', fg: '#065f46', label: 'Accepted' },
  approved: { bg: '#d1fae5', fg: '#065f46', label: 'Approved' },
  staged: { bg: '#fef3c7', fg: '#92400e', label: 'Staged' },
  paid: { bg: '#bbf7d0', fg: '#14532d', label: 'Paid' },
  rejected: { bg: '#fee2e2', fg: '#991b1b', label: 'Rejected' },
  overdue: { bg: '#fecaca', fg: '#7f1d1d', label: 'Overdue' },
  na: { bg: '#e5e7eb', fg: '#6b7280', label: 'N/A' },
};

const fmtUSD = (n: number | null | undefined) => {
  if (n == null || !isFinite(Number(n))) return '—';
  const v = Number(n);
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}k`;
  return `$${Math.round(v).toLocaleString()}`;
};
const usd0 = (n: number | null | undefined) =>
  n == null || !isFinite(Number(n)) ? '—' : `$${Math.round(Number(n)).toLocaleString()}`;

function StatusBadge({ status }: { status: string }) {
  const s = STATUS_BADGE[status] ?? STATUS_BADGE.pending;
  return <span style={{ background: s.bg, color: s.fg, padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>{s.label}</span>;
}

function StatCard({ label, value, sub, color }: { label: string; value: string | number; sub?: string; color?: string }) {
  return (
    <div style={{ background: '#fff', border: '1px solid var(--border, #e2e8f0)', borderTop: `3px solid ${color ?? '#3b82f6'}`, borderRadius: 6, padding: '12px 14px' }}>
      <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-muted, #64748b)' }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, marginTop: 2 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text-muted, #64748b)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

export default function DrawsPage() {
  const [projectId, setProjectId] = useState<string | null>(null);
  const [editing, setEditing] = useState<Milestone | null>(null);
  const qc = useQueryClient();
  const navigate = useNavigate();

  const { data: projects } = useQuery<Project[]>({
    queryKey: ['bead-projects'],
    queryFn: () => api.get('/bead/projects'),
  });
  const activeProjectId = projectId ?? projects?.[0]?.id ?? null;

  const { data: obligations = [] } = useQuery<Obligation[]>({
    queryKey: ['fin-obligations', activeProjectId],
    queryFn: () => api.get(`/bead-pm/projects/${activeProjectId}/obligations?category=federal_financial`),
    enabled: !!activeProjectId,
  });

  const { data: draws } = useQuery<DrawsSummary>({
    queryKey: ['draws-summary', activeProjectId],
    queryFn: () => api.get(`/bead/projects/${activeProjectId}/draws-summary`),
    enabled: !!activeProjectId,
  });

  const drawMut = useMutation({
    mutationFn: (v: { id: string; body: any; stagedFederal?: number }) => api.post(`/bead/draws/${v.id}/status`, v.body),
    onSuccess: (data: any, v: any) => {
      qc.invalidateQueries({ queryKey: ['draws-summary', activeProjectId] });
      if (v.body?.status === 'submitted' && data && v.stagedFederal != null && Number(data.federal_usd) < Number(v.stagedFederal)) {
        window.alert('Draw filed at ' + usd0(data.federal_usd) + ' federal — reduced from the staged ' + usd0(v.stagedFederal) + ' to your documented eligible cost. BEAD reimbursements cannot exceed actual cost incurred.');
      }
    },
    onError: (err: any) => {
      window.alert('Could not file this draw: ' + (err?.message ?? 'request failed') + '. This usually means the requested amount exceeds your documented eligible cost or the unlocked milestone ceiling — allocate more invoices to this phase, or complete the next milestone, then try again.');
    },
  });

  const drawObligations = useMemo(() =>
    obligations.filter(o =>
      ['FED-FIN-DRAW', 'FED-FIN-MATCH', 'FED-FIN-SF425', 'FED-FIN-CASH', 'FED-FIN-AUDIT', 'FED-FIN-EQUIP'].includes(o.code)
    ), [obligations]);

  const kpis = draws?.kpis;
  const federal = Number(kpis?.federal ?? draws?.project?.grant_amount_usd ?? 0);
  const energ = draws?.energization;
  const drawReqs = draws?.draws ?? [];

  // Federal draw bar segments (of the full federal award)
  const bar = useMemo(() => {
    const F = Math.max(1, federal);
    const paid = kpis?.paid_usd ?? 0;
    const inflight = Math.max(0, (kpis?.requested_usd ?? 0) - paid);
    const available = kpis?.available_usd ?? 0;              // unlocked − requested
    const locked = Math.max(0, F - (kpis?.unlocked_usd ?? 0)); // not yet unlocked by milestones
    return { paid, inflight, available, locked, F };
  }, [kpis, federal]);

  return (
    <div>
      <div className="page__header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <h1 className="page__title" style={{ margin: 0, fontSize: 24 }}>Draws</h1>
          <div className="page__subtitle" style={{ color: 'var(--text-muted, #64748b)', fontSize: 13 }}>
            Reimbursement draws, milestone schedule, and federal drawdown — driven by what you build
          </div>
        </div>
        <select value={activeProjectId ?? ''} onChange={(e) => setProjectId(e.target.value || null)}
          style={{ padding: '6px 10px', fontSize: 13, border: '1px solid var(--border, #e2e8f0)', borderRadius: 6 }}>
          {(projects ?? []).map(p => <option key={p.id} value={p.id}>{p.project_name} ({p.project_code})</option>)}
        </select>
      </div>

      {/* KPI cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 12, marginBottom: 14 }}>
        <StatCard label="Federal award" value={fmtUSD(federal)} color="#3b82f6" />
        <StatCard label="Match" value={fmtUSD(kpis?.match)} color="#8b5cf6" />
        <StatCard label="Built (service-available)" value={energ ? `${energ.pct}%` : '—'} sub={energ ? `${energ.lit.toLocaleString()}/${energ.total.toLocaleString()} homes` : undefined} color="#22c55e" />
        <StatCard label="Unlocked by milestones" value={fmtUSD(kpis?.unlocked_usd)} sub={`${kpis?.unlocked_pct ?? 0}% of federal`} color="#f59e0b" />
        <StatCard label="Drawn / requested" value={fmtUSD(kpis?.requested_usd)} sub={`${usd0(kpis?.paid_usd)} paid`} color="#10b981" />
        <StatCard label="Available to request" value={fmtUSD(kpis?.available_usd)} color="#0ea5e9" />
      </div>

      {/* Federal draw progress bar */}
      <div style={{ background: '#fff', border: '1px solid var(--border, #e2e8f0)', borderRadius: 8, padding: '14px 16px', marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: '#475569' }}>Federal drawdown</div>
          <div style={{ fontSize: 12, color: '#64748b' }}>{usd0((bar.paid + bar.inflight))} of {usd0(bar.F)} federal in flight</div>
        </div>
        <div style={{ display: 'flex', height: 16, borderRadius: 8, overflow: 'hidden', background: '#f1f5f9' }}>
          {bar.paid > 0 && <div title={`Paid ${usd0(bar.paid)}`} style={{ width: `${(bar.paid / bar.F) * 100}%`, background: '#16a34a' }} />}
          {bar.inflight > 0 && <div title={`Requested ${usd0(bar.inflight)}`} style={{ width: `${(bar.inflight / bar.F) * 100}%`, background: '#2563eb' }} />}
          {bar.available > 0 && <div title={`Available to request ${usd0(bar.available)}`} style={{ width: `${(bar.available / bar.F) * 100}%`, background: '#7dd3fc' }} />}
          {bar.locked > 0 && <div title={`Locked until milestones ${usd0(bar.locked)}`} style={{ width: `${(bar.locked / bar.F) * 100}%`, background: '#e2e8f0' }} />}
        </div>
        <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 11, color: '#64748b', flexWrap: 'wrap' }}>
          <Legend c="#16a34a" t={`Paid ${usd0(bar.paid)}`} />
          <Legend c="#2563eb" t={`Requested ${usd0(bar.inflight)}`} />
          <Legend c="#7dd3fc" t={`Available ${usd0(bar.available)}`} />
          <Legend c="#e2e8f0" t={`Locked until milestones ${usd0(bar.locked)}`} />
        </div>
      </div>

      {/* Draw requests ledger */}
      <section style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8 }}>
          <h2 style={{ fontSize: 16, margin: 0 }}>Draw requests · {drawReqs.length}</h2>
          <div style={{ fontSize: 12, color: '#64748b' }}>Staged: <b>{usd0(kpis?.staged_usd)}</b> · Actual invoiced: <b>{usd0(kpis?.actual_cost)}</b></div>
        </div>
        <div style={{ background: '#fff', border: '1px solid var(--border, #e2e8f0)', borderRadius: 6, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: '#f8fafc', borderBottom: '1px solid var(--border, #e2e8f0)' }}>
              <tr>{['Phase', 'Passings', 'Federal $', 'Match $', 'Actual invoiced', 'Status', 'Payment', ''].map(h =>
                <th key={h} style={{ padding: '8px 10px', textAlign: h.endsWith('$') || h === 'Passings' || h === 'Actual invoiced' ? 'right' : 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>{h}</th>
              )}</tr>
            </thead>
            <tbody>
              {drawReqs.length === 0 ? (
                <tr><td colSpan={8} style={{ padding: 20, color: '#94a3b8', textAlign: 'center' }}>
                  No draw requests yet. Energize a section on <b>Build → As-built</b> to stage a draw as you light homes.
                </td></tr>
              ) : drawReqs.map(d => (
                <tr key={d.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '8px 10px', fontWeight: 500 }}>{d.phase_name ?? 'Phase'}<div style={{ fontSize: 10, color: '#94a3b8' }}>{d.created_at?.slice(0, 10)}</div></td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{d.passings.toLocaleString()}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{usd0(d.federal_usd)}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#64748b' }}>{usd0(d.match_usd)}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{(d.actual_cost_usd ?? 0) > 0 ? <span style={{ fontWeight: 600, color: '#0f766e' }}>{usd0(d.actual_cost_usd)}</span> : <span style={{ color: '#cbd5e1' }} title="No invoices allocated to this phase yet">—</span>}</td>
                  <td style={{ padding: '8px 10px' }}><StatusBadge status={d.status} />{d.ready_reference && <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>{d.ready_reference}</div>}</td>
                  <td style={{ padding: '8px 10px' }}><StatusBadge status={d.payment_status} /></td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button onClick={() => navigate(`/draws/${d.id}/packet`)}
                      style={{ padding: '5px 10px', background: 'transparent', color: '#2563eb', border: '1px solid #bfdbfe', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', marginRight: 6 }}>Packet</button>
                    {d.status === 'staged' && (
                      <button onClick={() => drawMut.mutate({ id: d.id, body: { status: 'submitted' }, stagedFederal: d.federal_usd })} disabled={drawMut.isPending}
                        title="Files this draw against the state's Proof of Passings. The live Ready.net push activates once the BEAD reimbursement form key is provisioned."
                        style={{ padding: '5px 12px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>→ Request reimbursement</button>
                    )}
                    {d.status === 'submitted' && d.payment_status !== 'paid' && (
                      <button onClick={() => drawMut.mutate({ id: d.id, body: { status: 'accepted', payment_status: 'paid' } })} disabled={drawMut.isPending}
                        style={{ padding: '5px 12px', background: '#fff', color: '#16a34a', border: '1px solid #16a34a', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Mark paid</button>
                    )}
                    {d.payment_status === 'paid' && <span style={{ fontSize: 11, color: '#16a34a', fontWeight: 700 }}>✓ Reimbursed</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ padding: '6px 10px', fontSize: 11, color: '#64748b', borderTop: '1px solid var(--border)', background: '#f8fafc' }}>
            Draws stage automatically when you energize a section. A request files against the state's <b>Proof of Passings</b>; the live Ready.net reimbursement push activates once the grantor provisions the BEAD form key.
          </div>
        </div>
      </section>

      {/* Milestone schedule */}
      <section style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 16, marginBottom: 8 }}>Milestone draw schedule · {(draws?.milestones ?? []).length}</h2>
        <div style={{ background: '#fff', border: '1px solid var(--border, #e2e8f0)', borderRadius: 6, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: '#f8fafc', borderBottom: '1px solid var(--border, #e2e8f0)' }}>
              <tr>{['#', 'Milestone', 'Draw %', 'Federal $', 'Trigger', 'Status'].map(h =>
                <th key={h} style={{ padding: '8px 10px', textAlign: h === 'Draw %' || h === 'Federal $' ? 'right' : 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>{h}</th>
              )}</tr>
            </thead>
            <tbody>
              {!draws || draws.milestones.length === 0 ? (
                <tr><td colSpan={6} style={{ padding: 16, color: '#94a3b8', textAlign: 'center' }}>No milestone schedule for this project.</td></tr>
              ) : draws.milestones.map(m => {
                const done = m.effective_completed;
                return (
                  <tr key={m.id} onClick={() => setEditing(m)}
                    style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer', background: done ? '#f0fdf4' : 'transparent' }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                    onMouseLeave={(e) => e.currentTarget.style.background = done ? '#f0fdf4' : 'transparent'}>
                    <td style={{ padding: '8px 10px', color: '#94a3b8', fontFamily: 'ui-monospace, monospace' }}>{m.seq}</td>
                    <td style={{ padding: '8px 10px', fontWeight: 500 }}>
                      {done && <span style={{ color: '#16a34a', marginRight: 6 }}>✓</span>}{m.name}
                      {m.category && <span style={{ marginLeft: 8, fontSize: 10, color: '#94a3b8', textTransform: 'uppercase' }}>{m.category}</span>}
                    </td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{m.draw_pct != null ? `${Number(m.draw_pct).toFixed(0)}%` : '—'}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#64748b' }}>{usd0(m.draw_usd ?? (federal * Number(m.draw_pct ?? 0) / 100))}</td>
                    <td style={{ padding: '8px 10px', fontSize: 11 }}>
                      {m.auto
                        ? <span style={{ background: '#ecfeff', color: '#0e7490', padding: '2px 7px', borderRadius: 999, fontWeight: 600 }}>⚡ auto @ {m.auto_threshold}% built</span>
                        : <span style={{ color: '#94a3b8' }}>manual</span>}
                    </td>
                    <td style={{ padding: '8px 10px' }}>
                      {done ? <StatusBadge status="approved" /> : <StatusBadge status={m.status} />}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div style={{ padding: '6px 10px', fontSize: 11, color: '#64748b', borderTop: '1px solid var(--border)', background: '#f8fafc' }}>
            <b>⚡ Auto</b> milestones complete themselves as you light passings on the map. Click a <b>manual</b> milestone to mark it complete and attach the SF-425 / evidence.
          </div>
        </div>
      </section>

      {/* Draw-cycle obligations */}
      <section style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 16, marginBottom: 8 }}>Draw-cycle obligations · {drawObligations.length}</h2>
        <div style={{ background: '#fff', border: '1px solid var(--border, #e2e8f0)', borderRadius: 6, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: '#f8fafc', borderBottom: '1px solid var(--border, #e2e8f0)' }}>
              <tr>{['Code', 'Requirement', 'Authority', 'Cadence', 'Due', 'Status'].map(h =>
                <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>{h}</th>
              )}</tr>
            </thead>
            <tbody>
              {drawObligations.length === 0 ? (
                <tr><td colSpan={6} style={{ padding: 16, color: '#94a3b8', textAlign: 'center' }}>No draw-cycle obligations.</td></tr>
              ) : drawObligations.map(o => (
                <tr key={o.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '8px 10px', fontFamily: 'ui-monospace, monospace', fontSize: 11, color: '#64748b' }}>{o.code}</td>
                  <td style={{ padding: '8px 10px', fontWeight: 500 }}>{o.name}</td>
                  <td style={{ padding: '8px 10px', fontSize: 11, color: '#64748b' }}>{o.source_cite ?? '—'}</td>
                  <td style={{ padding: '8px 10px', fontSize: 11, color: '#64748b' }}>{o.cadence_rule?.replace(/_/g, ' ')}</td>
                  <td style={{ padding: '8px 10px', fontSize: 11, color: '#64748b' }}>{o.due_date ?? '—'}</td>
                  <td style={{ padding: '8px 10px' }}><StatusBadge status={o.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ padding: '6px 10px', fontSize: 11, color: '#64748b', borderTop: '1px solid var(--border)', background: '#f8fafc' }}>
            Update obligation status + attach evidence on <b>Compliance → Overview</b>.
          </div>
        </div>
      </section>

      {editing && activeProjectId && (
        <MilestoneEditor
          milestone={editing}
          projectId={activeProjectId}
          federalUsd={federal}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function Legend({ c, t }: { c: string; t: string }) {
  return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: c, display: 'inline-block' }} />{t}</span>;
}

// --------------------------------------------------------------------
// Milestone editor drawer
// --------------------------------------------------------------------

function MilestoneEditor({
  milestone, projectId, federalUsd, onClose,
}: { milestone: Milestone; projectId: string; federalUsd: number; onClose: () => void }) {
  const qc = useQueryClient();
  const [status, setStatus] = useState(milestone.status);
  const [completed, setCompleted] = useState(milestone.completed_date ?? '');
  const [target, setTarget] = useState(milestone.target_date ?? '');
  const [drawPct, setDrawPct] = useState(milestone.draw_pct != null ? String(milestone.draw_pct) : '');

  const saveMut = useMutation({
    mutationFn: (b: any) => api.put(`/bead/milestones/${milestone.id}`, b),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['draws-summary', projectId] }),
  });

  function save() {
    const body: any = { status };
    if (completed !== (milestone.completed_date ?? '')) body.completed_date = completed || null;
    if (target !== (milestone.target_date ?? '')) body.target_date = target || null;
    const dp = drawPct === '' ? null : Number(drawPct);
    if (dp !== milestone.draw_pct) body.draw_pct = dp;
    if (status === 'approved' && !completed && milestone.completed_date == null) {
      body.completed_date = new Date().toISOString().slice(0, 10);
    }
    saveMut.mutate(body, { onSuccess: () => onClose() });
  }

  const computedFederal = drawPct ? federalUsd * Number(drawPct) / 100 : null;

  return (
    <div onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.4)', zIndex: 100, display: 'flex', justifyContent: 'flex-end' }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ width: 480, maxWidth: '100%', background: 'white', height: '100vh', overflowY: 'auto', padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase' }}>Milestone {milestone.seq}</div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>{milestone.name}</div>
            {milestone.description && <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{milestone.description}</div>}
            {computedFederal != null && (
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>Federal $ at {drawPct}% = {fmtUSD(computedFederal)}</div>
            )}
            {milestone.auto && (
              <div style={{ fontSize: 11, color: '#0e7490', marginTop: 4 }}>⚡ Auto-completes at {milestone.auto_threshold}% of homes service-available — no manual action needed.</div>
            )}
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 0, fontSize: 24, cursor: 'pointer', color: '#64748b' }}>×</button>
        </div>

        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 4 }}>Status</div>
          <select value={status} onChange={(e) => setStatus(e.target.value as any)}
            style={{ width: '100%', padding: '6px 10px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 4 }}>
            <option value="pending">Pending</option>
            <option value="in_progress">In progress</option>
            <option value="submitted">Submitted (draw requested)</option>
            <option value="approved">Approved (drawn)</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 12 }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 4 }}>Target date</div>
            <input type="date" value={target} onChange={(e) => setTarget(e.target.value)}
              style={{ width: '100%', padding: '6px 10px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 4 }} />
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 4 }}>Completed date</div>
            <input type="date" value={completed} onChange={(e) => setCompleted(e.target.value)}
              style={{ width: '100%', padding: '6px 10px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 4 }} />
          </div>
        </div>

        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 4 }}>Draw %</div>
          <input type="number" value={drawPct} onChange={(e) => setDrawPct(e.target.value)} step={0.1} min={0} max={100}
            style={{ width: '100%', padding: '6px 10px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 4, fontVariantNumeric: 'tabular-nums' }} />
        </div>

        <div style={{ marginTop: 12 }}>
          <DocumentUploader
            value={null}
            onChange={() => { /* stored under project + milestone */ }}
            projectId={projectId}
            milestoneId={milestone.id}
            docType="milestone_evidence"
            label="Draw request / SF-425 / supporting evidence"
            hint="File is stored under this project + milestone."
          />
        </div>

        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={save} disabled={saveMut.isPending}
            style={{ padding: '6px 14px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            {saveMut.isPending ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
